import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, lastValueFrom } from 'rxjs';
import { map, tap, timeout } from 'rxjs/operators';

import { ClientFilterObject } from '@common/filter.decorator';

import { C } from 'src/app/@shared/constants';
import { ContentOptions, ContentService } from 'src/app/@core/content.service';
import { ProtoUrl } from 'src/app/@shared/protoUrl';

import { PersonModel, PersonSource } from 'src/app/person/@shared/person.model';

@Injectable({
  providedIn: 'root',
})
export class PersonService extends ContentService<PersonModel, PersonSource> {
  private path = 'persons';

  constructor(private http: HttpClient) {
    super();
  }

  public loadContent(id: string, options: ContentOptions<PersonSource> = {}): Observable<PersonModel[] | null> {
    if (!options.type) {
      console.warn('[PersonService] loadContent(id, options) no type provided -> defaults to loadListContent');
    }

    if (options.type !== 'detail') {
      return this.loadListContent(id, options);
    }

    // we only need the relation for detail view
    options.filter ||= {};
    options.filter.relations ||= [];
    options.filter.relations.push('organizations.organization');

    return this.loadDetailContent(id, options).pipe(map((item: PersonModel | null) => {
      return item
        ? [item]
        : null;
    }));
  }

  public create(attributes: Partial<PersonSource>, storeId: string): Promise<PersonModel> {
    return lastValueFrom(this.http.post<PersonSource>(ProtoUrl.getUrl(this.path), attributes)
      .pipe(map((item: PersonSource) => { return new PersonModel(item); }),
        tap((item: PersonModel) => {
          if (this.metadataStore[storeId]) {
            this.metadataStore[storeId].totalCount += 1;
          }

          this.updateItem(item, storeId, true);
        })));
  }

  public patch(id: string, attributes: Partial<PersonSource>): Promise<PersonModel> {
    return this.http.patch<PersonSource>(ProtoUrl.getUrl(`${this.path}/${id}`), attributes)
      .pipe(map((item: PersonSource) => { return new PersonModel(item); }),
        tap((item: PersonModel) => {
          this.updateItem(item);
        }))
      .toPromise();
  }

  public delete(id: string): Promise<boolean> {
    return this.http.delete<PersonSource>(ProtoUrl.getUrl(`${this.path}/${id}`))
      .pipe(map(() => {
        return true;
      }),
      tap(() => {
        this.removeItem(id);
      }))
      .toPromise();
  }

  protected loadListContent(id: string, options: ContentOptions<PersonSource>): Observable<PersonModel[]> {
    const filter: ClientFilterObject<PersonSource> = this.getNormalizedFilter(id, options.filter);
    const url = ProtoUrl.getUrl(this.path, {
      filter,
    });

    console.info('[PersonService] load items', url);
    let totalCount: number;

    return this.http.get<PersonSource[]>(url, {
      observe: 'response',
    })
      .pipe(
        timeout(C.DEFAULT_TIMEOUT),
        tap((response: HttpResponse<PersonSource[]>) => {
          const { headers } = response;

          totalCount = parseInt(headers.get('x-total-count')) || (response.body?.length || 0);
        }),
        map((response: HttpResponse<PersonSource[]>) => { return response.body || []; }),
        map((items: PersonSource[]) => { return items.map((item) => { return new PersonModel(item); }); }),
        tap((items: PersonModel[]) => {
          return this.setItems(items, id, filter, totalCount);
        }),
      );
  }

  protected loadDetailContent(id: string, options: ContentOptions<PersonSource>): Observable<PersonModel> {
    const { filter } = options;
    const url = ProtoUrl.getUrl(`${this.path}/${id}`, {
      filter,
    });

    return this.http.get<PersonSource>(url)
      .pipe(timeout(C.DEFAULT_TIMEOUT),
        map((item) => {
          return item
            ? new PersonModel(item)
            : null;
        }),
        tap((item) => {
          this.updateItem(item);
        }));
  }
}
